import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { setDeviceFilter, DeviceFilter } from "../../store/dashboardSlice.ts";
import { Site, Organization, IQWatchDeviceWithData } from "../../types";
import IQWatchSummaryCards from "./IQWatchSummaryCards.tsx";
import IQWatchDeviceTable from "./IQWatchDeviceTable.tsx";
import IQWatchDeviceDetail from "./IQWatchDeviceDetail.tsx";
import Pagination from "../Pagination.tsx";

interface DashboardIQWatchDeviceListProps {
  site: Site | null;
  organization?: Organization | null; // Add organization prop back
  orgId?: string;
  iqwatchDevices: IQWatchDeviceWithData[];
  loading: boolean;
  error: string | null;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  summary?: {
    total: number;
    online: number;
    offline: number;
    low_battery: number;
  } | null;
  onPageChange?: (page: number) => void;
  isAllOrganizationsMode?: boolean; // Add flag for "All Organizations" mode
  searchTerm?: string; // Add search term prop
  onDeviceSelect?: () => void; // Callback when device is selected
  onDeviceDeselect?: () => void; // Callback when device detail is closed
}

const DashboardIQWatchDeviceList: React.FC<DashboardIQWatchDeviceListProps> = ({
  site,
  organization,
  orgId,
  iqwatchDevices,
  loading,
  error,
  pagination,
  summary,
  onPageChange,
  isAllOrganizationsMode = false,
  searchTerm = "",
  onDeviceSelect,
  onDeviceDeselect,
}) => {
  const dispatch = useDispatch();
  const deviceFilter = useSelector(
    (state: RootState) => state.dashboard.deviceFilter
  );
  const [selectedDevice, setSelectedDevice] =
    useState<IQWatchDeviceWithData | null>(null);

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // When browser back button is pressed, close the device detail
      if (selectedDevice) {
        setSelectedDevice(null);
        onDeviceDeselect?.(); // Notify parent that device detail is being closed
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [selectedDevice, onDeviceDeselect]);

  const handleDeviceDoubleClick = (deviceWithData: IQWatchDeviceWithData) => {
    // Push state to browser history when opening device detail
    // This creates a history entry so the back button will work
    window.history.pushState(
      { deviceId: deviceWithData.device.id, view: "deviceDetail" },
      "",
      window.location.href
    );
    setSelectedDevice(deviceWithData);
    onDeviceSelect?.(); // Notify parent that device detail is being shown
  };

  const handleBackToList = () => {
    // Go back in browser history, which will trigger popstate event
    // The popstate handler will close the device detail
    window.history.back();
    onDeviceDeselect?.(); // Notify parent that device detail is being closed
  };

  const handleFilterChange = (filter: DeviceFilter) => {
    dispatch(setDeviceFilter(filter));
  };

  // Determine if we're in "All Sites" mode or "All Organizations" mode
  const isAllSitesMode = !site && organization && !isAllOrganizationsMode;

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading devices...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  // Show detail view if a device is selected
  if (selectedDevice) {
    return (
      <div className="p-2">
        <IQWatchDeviceDetail
          deviceWithData={selectedDevice}
          onBack={handleBackToList}
        />
      </div>
    );
  }

  return (
    <div className="p-2">
      {/* Summary Cards - Always show when in list view, even if no devices */}
      {(site || isAllSitesMode || isAllOrganizationsMode) && (
        <div className="sticky top-0 z-10 bg-gray-50 -mx-2 px-2 py-1 border-b border-gray-200">
          <IQWatchSummaryCards
            devices={iqwatchDevices}
            currentFilter={deviceFilter}
            onFilterChange={handleFilterChange}
            summary={summary || undefined}
            pagination={pagination || undefined}
          />
        </div>
      )}

      {/* Device Table */}
      {site || isAllSitesMode || isAllOrganizationsMode ? (
        <div className="mt-1">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {iqwatchDevices.length > 0 ? (
              <>
                <IQWatchDeviceTable
                  devices={iqwatchDevices}
                  onDeviceDoubleClick={handleDeviceDoubleClick}
                  showSiteColumn={isAllSitesMode || isAllOrganizationsMode} // Show site column when displaying all sites or all organizations
                  showOrganizationColumn={isAllOrganizationsMode} // Show organization column when displaying all organizations
                  deviceFilter={deviceFilter}
                />

                {/* 添加分页信息显示 */}
                {pagination && (
                  <div className="px-4 py-2 text-sm text-gray-600 border-t border-gray-200">
                    Displaying {(pagination.page - 1) * pagination.limit + 1} -{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total
                    )}{" "}
                    of {pagination.total} Devices
                    {isAllOrganizationsMode && (
                      <span className="ml-2 text-indigo-600 font-medium">
                        (from all customers)
                      </span>
                    )}
                    {isAllSitesMode && (
                      <span className="ml-2 text-blue-600 font-medium">
                        (from all sites in {organization?.name})
                      </span>
                    )}
                    {pagination.total_pages > 1 && (
                      <span className="ml-2">
                        ({pagination.total_pages} Pages)
                      </span>
                    )}
                    <div className="mt-1 text-xs text-gray-500">
                      💡 Click on any device to view detailed information
                    </div>
                  </div>
                )}

                {/* Pagination - 修改条件，即使只有1页也显示分页信息 */}
                {pagination && onPageChange && pagination.total > 0 && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    hasNext={pagination.has_next}
                    hasPrev={pagination.has_prev}
                    onPageChange={onPageChange}
                    loading={loading}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No devices found
                  </h3>
                  <p className="text-gray-600">
                    {deviceFilter === "offline"
                      ? "No offline devices match the current filter. Click 'All Devices' to view all devices."
                      : deviceFilter === "online"
                      ? "No online devices match the current filter. Click 'All Devices' to view all devices."
                      : deviceFilter === "low_battery"
                      ? "No devices with low battery match the current filter. Click 'All Devices' to view all devices."
                      : "No devices are currently available."}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Please select a site to view its devices.
        </div>
      )}
    </div>
  );
};

export default DashboardIQWatchDeviceList;
